import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, Mail, Calculator } from 'lucide-react';
import { scrollToSection, makePhoneCall, sendEmail } from '../utils/buttonActions';
import { mockData } from '../data/mockData';
import ThemeToggle from './ThemeToggle';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'HOME', href: 'home' },
    { name: 'SERVICES', href: 'services' },
    { name: 'QUOTE', href: 'smart-quote' },
    { name: 'PORTFOLIO', href: 'portfolio' },
    { name: 'ABOUT', href: 'about' },
    { name: 'CONTACT', href: 'contact' }
  ];

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    setIsMenuOpen(false);
  };

  return (
    <>
      {/* Top Bar */}
      <div className="bg-[#0A0A0A] text-white py-2 px-4 border-b border-[#333]" data-testid="top-bar">
        <div className="max-w-7xl mx-auto flex justify-between items-center text-xs tracking-widest uppercase font-medium">
          <div className="flex items-center space-x-6">
            <button
              onClick={() => makePhoneCall()}
              className="min-h-11 flex items-center space-x-2 hover:text-[#FF2A00] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF2A00]"
              data-testid="top-bar-phone"
            >
              <Phone className="h-3 w-3" strokeWidth={2} />
              <span>{mockData.contact.phone}</span>
            </button>
            <button
              onClick={() => sendEmail()}
              className="hidden sm:flex min-h-11 items-center space-x-2 hover:text-[#FF2A00] transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF2A00]"
              data-testid="top-bar-email"
            >
              <Mail className="h-3 w-3" strokeWidth={2} />
              <span>{mockData.contact.email}</span>
            </button>
          </div>
          <div className="top-bar-meta hidden md:block text-[#888]">
            {mockData.contact.address.city}, {mockData.contact.address.state} | {mockData.contact.businessHours}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header
        className={`sticky top-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm border-b-2 border-[#0A0A0A] ${
          isScrolled ? 'shadow-brutal-sm' : ''
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <button
              type="button"
              className="min-h-11 flex items-center space-x-3 cursor-pointer group text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF2A00]"
              onClick={() => handleNavClick('home')}
              data-testid="header-logo"
            >
              <div className="brand-logo-frame">
                <img src="/aim-logo.png" alt="Aim Flex" className="h-10 w-auto object-contain" />
              </div>
              <div>
                <h1 className="brand-wordmark text-lg font-black tracking-tighter text-[#0A0A0A] uppercase leading-none">
                  {mockData.company.name}
                </h1>
                <p className="brand-tagline text-[10px] tracking-[0.2em] uppercase text-[#666] font-bold">The Printing People</p>
              </div>
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="min-h-11 text-xs font-bold tracking-[0.15em] text-[#0A0A0A] hover:text-[#FF2A00] transition-colors duration-200 relative group focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF2A00]"
                  data-testid={`nav-${item.href}`}
                >
                  {item.name}
                  <span className="absolute -bottom-1 left-0 w-0 h-[2px] bg-[#FF2A00] group-hover:w-full transition-all duration-300"></span>
                </button>
              ))}
            </nav>

            {/* CTA + Mobile Toggle */}
            <div className="flex items-center space-x-3">
              <ThemeToggle className="hidden sm:inline-flex" />

              <button
                onClick={() => handleNavClick('smart-quote')}
                className="hidden md:flex min-h-11 items-center bg-[#FF2A00] text-white px-5 py-2 text-xs font-bold tracking-widest uppercase border-2 border-[#0A0A0A] shadow-brutal-sm hover:shadow-none hover:translate-x-[2px] hover:translate-y-[2px] transition-all duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF2A00]"
                data-testid="header-quote-btn"
              >
                <Calculator className="h-3 w-3 mr-2" strokeWidth={2} />
                SMART QUOTE
              </button>

              <button
                className="lg:hidden min-h-11 min-w-11 p-2 border-2 border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#FF2A00]"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle menu"
                data-testid="mobile-menu-toggle"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className={`mobile-nav-shell lg:hidden transition-all duration-300 overflow-hidden border-t-2 border-[#0A0A0A] ${
            isMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0 border-t-0'
          }`}>
            <nav className="py-4 space-y-1">
              <div className="px-2 pb-3">
                <ThemeToggle mobile />
              </div>
              {navItems.map((item) => (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href)}
                  className="block w-full text-left py-3 px-2 text-sm font-bold tracking-widest uppercase text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white transition-colors duration-200"
                  data-testid={`mobile-nav-${item.href}`}
                >
                  {item.name}
                </button>
              ))}
              <button
                onClick={() => handleNavClick('smart-quote')}
                className="w-full bg-[#FF2A00] text-white py-3 mt-2 text-sm font-bold tracking-widest uppercase border-2 border-[#0A0A0A] shadow-brutal-sm"
                data-testid="mobile-quote-btn"
              >
                SMART QUOTE
              </button>
            </nav>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
